"use client";

import { c as _c } from "react-compiler-runtime";
import { useState } from "react";
import type { DisplayAgelessAssessment } from "@/core/api/orval/model";
import { useCurrentAssessment } from "@/features/assessments/AssessmentSessionProvider";
import { assessmentSessionStorageService } from "@/features/assessments/AssessmentSessionStorageService";
import type { AssessmentStudioRetakeTarget } from "@/features/assessments/AssessmentSessionStorageService";
import { useAssessmentStep } from "@/features/assessments/AssessmentStepProvider/AssessmentStepProvider";
import { ConsentStage } from "@/features/assessments/components/ImageCaptureStep/stages/ConsentStage";
import type { FrontCaptureResult } from "@/features/assessments/components/ImageCaptureStep/types";
import { hasAssessmentConsentInCache } from "@/features/assessments/consent/consentUtils";
import { useDemoMode } from "@/features/assessments/demo/useDemoMode";
import { FaceLandmarkerProvider } from "@/features/facialRecognition";
import { LashBrowCaptureStage } from "../../lashBrow/imageCapture/LashBrowCaptureStage";
import { LashBrowConfirmStage } from "../../lashBrow/imageCapture/LashBrowConfirmStage";
import { LashBrowDemoCaptureStage } from "../../lashBrow/imageCapture/LashBrowDemoCaptureStage";
import { LashBrowPhotoTipsOverlay } from "../../lashBrow/imageCapture/LashBrowPhotoTipsOverlay";
import { LashBrowPhotoUploadStage } from "../../lashBrow/imageCapture/LashBrowPhotoUploadStage";

// Where consent hands off once granted. Unlike the aesthetics upload flow, LashBrowPhotoUploadStage
// has no consent gate of its own, so the upload route has to carry its destination through consent.
type LashBrowPostConsentStage = {
  stage: "demo";
} | {
  stage: "frontCapture";
} | {
  stage: "frontUpload";
};
type LashBrowImageCaptureStage = {
  stage: "consent";
  next: LashBrowPostConsentStage;
} | {
  stage: "demo";
} | {
  stage: "tips";
} | {
  stage: "frontCapture";
  retake?: boolean;
} | {
  stage: "frontUpload";
} | {
  stage: "confirm";
  frontResult: FrontCaptureResult;
};
export function LashBrowImageCaptureStep() {
  const $ = _c(15);
  const {
    goToNextStep,
    goToStart,
    flowType,
    subdomainSlug
  } = useAssessmentStep();
  const {
    assessment,
    headers,
    isLoadingAssessment
  } = useCurrentAssessment();
  let t0;
  if ($[0] !== flowType || $[1] !== subdomainSlug) {
    t0 = {
      subdomainSlug,
      flowType
    };
    $[0] = flowType;
    $[1] = subdomainSlug;
    $[2] = t0;
  } else {
    t0 = $[2];
  }
  const isDemoMode = useDemoMode(t0);
  if (isLoadingAssessment || !assessment) {
    return null;
  }
  let t1;
  if ($[3] !== assessment.frontFaceImageUrl || $[4] !== assessment.id || $[5] !== assessment.uploadedFrontFaceImage || $[6] !== flowType) {
    t1 = () => Boolean(assessment.frontFaceImageUrl) || Boolean(assessment.uploadedFrontFaceImage) || hasAssessmentConsentInCache(assessment.id, flowType);
    $[3] = assessment.frontFaceImageUrl;
    $[4] = assessment.id;
    $[5] = assessment.uploadedFrontFaceImage;
    $[6] = flowType;
    $[7] = t1;
  } else {
    t1 = $[7];
  }
  const hasCapturedConsent = t1;
  let t2;
  if ($[8] !== assessment || $[9] !== goToNextStep || $[10] !== goToStart || $[11] !== hasCapturedConsent || $[12] !== headers || $[13] !== isDemoMode) {
    t2 = <LashBrowImageCaptureFlowInner assessment={assessment} goToNextStep={goToNextStep} goToStart={goToStart} hasCapturedConsent={hasCapturedConsent} headers={headers} isDemoMode={isDemoMode} />;
    $[8] = assessment;
    $[9] = goToNextStep;
    $[10] = goToStart;
    $[11] = hasCapturedConsent;
    $[12] = headers;
    $[13] = isDemoMode;
    $[14] = t2;
  } else {
    t2 = $[14];
  }
  return t2;
}
type LashBrowImageCaptureFlowProps = {
  assessment: DisplayAgelessAssessment;
  headers: HeadersInit;
  hasCapturedConsent: () => boolean;
  isDemoMode: boolean;
  goToNextStep: () => void;
  goToStart: () => void;
};
function LashBrowImageCaptureFlowInner(t0) {
  const $ = _c(14);
  const {
    assessment,
    headers,
    hasCapturedConsent,
    isDemoMode,
    goToNextStep,
    goToStart
  } = t0;
  const requestedRetake = assessmentSessionStorageService.getPersistedStudioCapture(assessment.id)?.requestedRetake;
  let t1;
  if ($[0] !== hasCapturedConsent || $[1] !== isDemoMode || $[2] !== requestedRetake) {
    t1 = () => getInitialLashBrowStage({
      hasCapturedConsent,
      isDemoMode,
      requestedRetake
    });
    $[0] = hasCapturedConsent;
    $[1] = isDemoMode;
    $[2] = requestedRetake;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [current, setCurrent] = useState(t1);
  const requiresFaceLandmarker = current.stage !== "consent" && current.stage !== "tips";
  let t2;
  if ($[4] !== assessment || $[5] !== current || $[6] !== goToNextStep || $[7] !== goToStart || $[8] !== hasCapturedConsent || $[9] !== headers) {
    t2 = renderLashBrowCaptureStage(current, setCurrent, {
      assessment,
      headers,
      goToNextStep,
      goToStart,
      hasCapturedConsent
    });
    $[4] = assessment;
    $[5] = current;
    $[6] = goToNextStep;
    $[7] = goToStart;
    $[8] = hasCapturedConsent;
    $[9] = headers;
    $[10] = t2;
  } else {
    t2 = $[10];
  }
  let t3;
  if ($[11] !== requiresFaceLandmarker || $[12] !== t2) {
    t3 = <FaceLandmarkerProvider enabled={requiresFaceLandmarker} tooCloseFaceSizeMax={0.6}>{t2}</FaceLandmarkerProvider>;
    $[11] = requiresFaceLandmarker;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
}
function getInitialLashBrowStage({
  hasCapturedConsent,
  isDemoMode,
  requestedRetake
}: {
  hasCapturedConsent: () => boolean;
  isDemoMode: boolean;
  requestedRetake: AssessmentStudioRetakeTarget | null | undefined;
}): LashBrowImageCaptureStage {
  // Lash/brow captures a single front photo, so the flow only ever persists a "front" retake
  // request (see LashBrowAnalysisStep / LashBrowConfirmStage). "profile"/"both" are aesthetics-only
  // targets that never reach a lash/brow assessment.
  if (requestedRetake === "front") {
    return {
      stage: "frontCapture",
      retake: true
    };
  }
  // Demo mode has no tips sheet to show first, so it keeps the consent-first entry.
  if (isDemoMode) {
    return hasCapturedConsent() ? {
      stage: "demo"
    } : {
      stage: "consent",
      next: {
        stage: "demo"
      }
    };
  }
  return {
    stage: "tips"
  };
}
type LashBrowStageActions = Pick<LashBrowImageCaptureFlowProps, "assessment" | "headers" | "hasCapturedConsent" | "goToNextStep" | "goToStart">;
function renderLashBrowCaptureStage(current: LashBrowImageCaptureStage, setCurrent: (stage: LashBrowImageCaptureStage) => void, {
  assessment,
  headers,
  goToNextStep,
  goToStart,
  hasCapturedConsent
}: LashBrowStageActions): React.ReactElement {
  switch (current.stage) {
    // Outside demo mode this is reached from the tips sheet, never before it: the visitor sees
    // what we are about to capture before being asked to consent to us capturing it.
    case "consent":
      return <div className="relative flex min-h-dvh w-full flex-1 flex-col overflow-hidden bg-black/90">
          <ConsentStage assessment={assessment} headers={headers} onAccepted={() => setCurrent(current.next)} onDeclined={goToStart} />
        </div>;
    case "demo":
      return <LashBrowDemoCaptureStage assessment={assessment} headers={headers} onComplete={goToNextStep} />;
    case "tips":
      return <div className="relative flex min-h-dvh w-full flex-1 flex-col overflow-hidden bg-black/90">
          <LashBrowPhotoTipsOverlay open onStart={() => setCurrent(hasCapturedConsent() ? {
          stage: "frontCapture"
        } : {
          stage: "consent",
          next: {
            stage: "frontCapture"
          }
        })} onUploadInstead={() => setCurrent(hasCapturedConsent() ? {
          stage: "frontUpload"
        } : {
          stage: "consent",
          next: {
            stage: "frontUpload"
          }
        })} />
        </div>;
    case "frontUpload":
      return <LashBrowPhotoUploadStage assessment={assessment} headers={headers} onBackToTips={() => setCurrent({
        stage: "tips"
      })} onComplete={(result: FrontCaptureResult) => {
        setCurrent({
          stage: "confirm",
          frontResult: result
        });
      }} />;
    case "frontCapture":
      return <LashBrowCaptureStage assessment={assessment} headers={headers} platformId={assessment.platformId} skipIntro={current.retake} onComplete={(result: FrontCaptureResult) => {
        setCurrent({
          stage: "confirm",
          frontResult: result
        });
      }} />;
    case "confirm":
      return <LashBrowConfirmStage assessment={assessment} frontResult={current.frontResult} headers={headers} onConfirmed={goToNextStep} onRetake={() => setCurrent({
        stage: "frontCapture",
        retake: true
      })} />;
  }
}